<template>
  <div >
    <!-- Chat Window -->
    <v-card class="chat-window" elevation="8" style="display: contents;">
      <!-- Chat Header -->
      <v-card-title class="chat-header orange darken-1 white--text pa-3">
        <v-avatar size="32" class="mr-2">
          <v-icon color="white">mdi-robot</v-icon>
        </v-avatar>
        <div class="flex-grow-1">
          <div class="font-weight-bold">AI Personality Setup</div>
          <div class="caption">Let's create your digital twin</div>
        </div>
        <v-btn icon small @click="$emit('close')" class="ml-2">
          <v-icon color="white">mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <!-- Chat Messages -->
      <v-card-text class="chat-messages pa-0" style="height: 400px; overflow-y: auto;">
        <div class="pa-3">
          <div v-for="(message, index) in messages" :key="index" class="message-container mb-3">
            <!-- AI Message -->
            <div v-if="message.type === 'ai'" class="d-flex align-start">
              <v-avatar size="32" class="mr-2">
                <v-icon color="orange">mdi-robot</v-icon>
              </v-avatar>
              <div class="message-bubble ai-message">
                <div v-html="formatMessage(message.text)"></div>
              </div>
            </div>
            
            <!-- User Message -->
            <div v-else class="d-flex align-end justify-end">
              <div class="message-bubble user-message">
                {{ message.text }}
              </div>
              <v-avatar size="32" class="ml-2">
                <v-icon color="blue">mdi-account</v-icon>
              </v-avatar>
            </div>
          </div>

          <!-- Typing Indicator -->
          <div v-if="isTyping" class="d-flex align-start mb-3">
            <v-avatar size="32" class="mr-2">
              <v-icon color="orange">mdi-robot</v-icon>
            </v-avatar>
            <div class="typing-indicator">
              <div class="typing-dots">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          </div>
        </div>
      </v-card-text>

      <!-- Chat Input -->
      <v-card-actions class="chat-input pa-3" v-if="!completed">
        <v-text-field
          v-model="userInput"
          placeholder="Type your response..."
          outlined
          dense
          hide-details
          @keyup.enter="sendMessage"
          :disabled="isTyping"
          class="mr-2"
        ></v-text-field>
        <v-btn
          color="orange"
          @click="sendMessage"
          :disabled="!userInput.trim() || isTyping"
          icon
        >
          <v-icon class="material-icons-round">send</v-icon>
        </v-btn>
      </v-card-actions>

      <!-- Completion Actions -->
      <v-card-actions v-if="completed && personalitySummary" class="pa-3">
        <div class="w-100">
          <v-divider class="mb-3"></v-divider>
          <h3 class="mb-2">Your AI Personality Summary:</h3>
          <div class="personality-summary mb-3 pa-3" style="background: #f5f5f5; border-radius: 8px;">
            <div v-html="formatMessage(personalitySummary)"></div>
          </div>
          <div class="d-flex justify-space-between">
            <v-btn color="grey" outlined @click="editSummary">
              <v-icon left>mdi-pencil</v-icon>
              Edit
            </v-btn>
            <v-btn color="orange" @click="approveSummary" :loading="approving">
              <v-icon left>mdi-check</v-icon>
              Approve & Activate
            </v-btn>
          </div>
        </div>
      </v-card-actions>
    </v-card>

    <!-- Edit Summary Dialog -->
    <v-dialog v-model="editDialog" max-width="600px">
      <v-card>
        <v-card-title>Edit Your Personality Summary</v-card-title>
        <v-card-text>
          <v-textarea
            v-model="editedSummary"
            label="Personality Summary"
            rows="8"
            outlined
          ></v-textarea>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="editDialog = false">Cancel</v-btn>
          <v-btn color="orange" dark @click="saveSummary" :loading="saving">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>

export default {
  name: 'PersonalityOnboardingChat',
  data() {
    return {
      messages: [],
      userInput: '',
      isTyping: false,
      currentStep: 'greeting_style',
      completed: false,
      personalitySummary: '',
      agentLink: '',
      editDialog: false,
      editedSummary: '',
      approving: false,
      saving: false
    }
  },
  mounted() {
    this.startOnboarding()
  },
  methods: {
    async startOnboarding() {
      try {
        const response = await axios.get('/api/personality/start-onboarding', {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        })

        if (response.data.flag === 1) {
          if (response.data.data.completed) {
            this.completed = true
            this.personalitySummary = response.data.data.summary
            this.addMessage('ai', 'Your personality onboarding is already completed!')
          } else {
            this.addMessage('ai', response.data.data.greeting)
            this.currentStep = response.data.data.step
          }
        }
      } catch (error) {
        console.error('Error starting onboarding:', error)
        this.addMessage('ai', 'Sorry, there was an error starting the onboarding. Please try again.')
      }
    },

    async sendMessage() {
      if (!this.userInput.trim()) return

      const message = this.userInput.trim()
      this.addMessage('user', message)
      this.userInput = ''
      this.isTyping = true

      try {
        const response = await axios.post('/api/personality/process-response', {
          message: message,
          step: this.currentStep
        }, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        })

        if (response.data.flag === 1) {
          setTimeout(() => {
            this.isTyping = false
            this.addMessage('ai', response.data.message)
            this.currentStep = response.data.data.step
            
            if (response.data.data.completed) {
              this.completeOnboarding()
            }
          }, 1500) // Simulate typing delay
        }
      } catch (error) {
        this.isTyping = false
        console.error('Error processing response:', error)
        this.addMessage('ai', 'Sorry, there was an error processing your response. Please try again.')
      }
    },

    async completeOnboarding() {
      try {
        const response = await axios.post('/api/personality/complete-onboarding', {}, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        })

        if (response.data.flag === 1) {
          this.completed = true
          this.personalitySummary = response.data.data.summary
          this.agentLink = response.data.data.agent_link
          this.addMessage('ai', `Perfect! I've created your personality profile. Here's your unique AI assistant link: ${this.agentLink}`)
        }
      } catch (error) {
        console.error('Error completing onboarding:', error)
        this.addMessage('ai', 'There was an error completing your onboarding. Please try again.')
      }
    },

    addMessage(type, text) {
      this.messages.push({ type, text })
      this.$nextTick(() => {
        const chatMessages = this.$el.querySelector('.chat-messages')
        if (chatMessages) {
          chatMessages.scrollTop = chatMessages.scrollHeight
        }
      })
    },

    formatMessage(text) {
      return text.replace(/\n/g, '<br>')
    },

    editSummary() {
      this.editedSummary = this.personalitySummary
      this.editDialog = true
    },

    async saveSummary() {
      this.saving = true
      try {
        const response = await axios.post('/api/personality/update', {
          summary: this.editedSummary
        }, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        })

        if (response.data.flag === 1) {
          this.personalitySummary = response.data.data.summary
          this.editDialog = false
          this.$toast.success('Personality summary updated successfully!')
        }
      } catch (error) {
        console.error('Error updating summary:', error)
        this.$toast.error('Error updating summary. Please try again.')
      } finally {
        this.saving = false
      }
    },

    async approveSummary() {
      this.approving = true
      try {
        const response = await axios.post('/api/personality/approve', {}, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        })

        if (response.data.flag === 1) {
          this.$toast.success('Personality approved! Your AI assistant is now active.')
          this.$emit('completed', {
            summary: this.personalitySummary,
            agentLink: this.agentLink
          })
        }
      } catch (error) {
        console.error('Error approving personality:', error)
        this.$toast.error('Error approving personality. Please try again.')
      } finally {
        this.approving = false
      }
    }
  }
}
</script>

<style scoped>
.chat-window {
  width: 100%;
  max-width: 500px;
  margin: 0 auto;
}

.message-bubble {
  max-width: 80%;
  padding: 12px 16px;
  border-radius: 18px;
  word-wrap: break-word;
}

.ai-message {
  background-color: #f5f5f5;
  color: #333;
}

.user-message {
  background-color: #2196F3;
  color: white;
}

.typing-indicator {
  background-color: #f5f5f5;
  padding: 12px 16px;
  border-radius: 18px;
}

.typing-dots {
  display: flex;
  gap: 4px;
}

.typing-dots span {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: #999;
  animation: typing 1.4s infinite ease-in-out;
}

.typing-dots span:nth-child(1) { animation-delay: -0.32s; }
.typing-dots span:nth-child(2) { animation-delay: -0.16s; }

@keyframes typing {
  0%, 80%, 100% { transform: scale(0); }
  40% { transform: scale(1); }
}

.personality-summary {
  font-size: 14px;
  line-height: 1.5;
}
</style>
